import React, { useState } from "react";
import { useParams } from "react-router-dom";

import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
  Chip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";

import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import UpdateRoundedIcon from "@mui/icons-material/UpdateRounded";

import {candidateApi} from "../../api/candidateApi";

const reasons = [
  "Need additional preparation time",
  "Medical emergency",
  "Technical / internet issue",
  "Power outage",
  "Personal emergency",
  "Schedule conflict",
  "Other",
];

export default function CandidateRescheduleRequestPage() {

  /**
   * ROUTE:
   * /interview/reschedule/:interview_id/:recruiter_id
   */
  const { interview_id, recruiter_id } = useParams();

  const [formData, setFormData] = useState({
    available_date: "",
    reason: "",
    customReason: "",
    comment: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setErrorMessage("");

      const payload = {
        interview_id,
        recruiter_id,
        available_date: formData.available_date,
        reschedule_reason:
          formData.reason === "Other"
            ? formData.customReason
            : formData.reason,
        comment: formData.comment,
      };

      const response =
        await candidateApi.requestReschedule(payload);

      if (response.success) {
        setSubmitted(true);
      } else {
        setErrorMessage(
          response.message ||
            "Unable to submit reschedule request."
        );
      }

    } catch (error) {
      console.error(error);

      setErrorMessage(
        "Something went wrong while submitting request."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#f8fafc",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
        }}
      >
        <Card
          sx={{
            width: "100%",
            maxWidth: 620,
            borderRadius: 5,
            boxShadow: "0 20px 60px rgba(15,23,42,0.08)",
          }}
        >
          <CardContent sx={{ p: { xs: 4, md: 6 } }}>
            <Stack spacing={3} alignItems="center" textAlign="center">
              <Avatar
                sx={{
                  width: 76,
                  height: 76,
                  bgcolor: alpha("#16a34a", 0.12),
                  color: "#16a34a",
                }}
              >
                <CheckCircleRoundedIcon fontSize="large" />
              </Avatar>

              <Box>
                <Typography variant="h4" fontWeight={800}>
                  Request Submitted
                </Typography>

                <Typography
                  color="text.secondary"
                  mt={1.5}
                  lineHeight={1.9}
                >
                  Your interview reschedule request has been
                  submitted successfully.
                </Typography>
              </Box>

              <Alert
                severity="info"
                sx={{
                  width: "100%",
                  borderRadius: 3,
                }}
              >
                The recruiter will review your request and
                notify you via email.
              </Alert>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f8fafc",
        py: { xs: 4, md: 8 },
        px: 2,
      }}
    >
      <Box maxWidth="1100px" mx="auto">
        <Grid container spacing={4}>
          
          {/* LEFT SECTION */}
          <Grid item xs={12} md={4}>
            <Stack spacing={3}>
              
              {/* HEADER */}
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  sx={{
                    bgcolor: "#2563eb",
                    width: 54,
                    height: 54,
                    fontWeight: 700,
                  }}
                >
                  M
                </Avatar>

                <Box>
                  <Typography variant="h5" fontWeight={800}>
                    MeruBot
                  </Typography>

                  <Typography color="text.secondary">
                    Interview Reschedule
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </Grid>

          {/* RIGHT SECTION */}
          <Grid item xs={12} md={8}>
            <Card
              sx={{
                borderRadius: 5,
                boxShadow: "0 10px 40px rgba(15,23,42,0.06)",
              }}
            >
              <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                <Stack spacing={4}>

                  <Box>
                    <Typography variant="h4" fontWeight={800}>
                      Request Interview Reschedule
                    </Typography>

                    <Typography
                      color="text.secondary"
                      mt={1}
                      lineHeight={1.8}
                    >
                      Provide your preferred availability and
                      reason for rescheduling the interview.
                    </Typography>
                  </Box>

                  {/* ERROR */}
                  {errorMessage && (
                    <Alert severity="error">
                      {errorMessage}
                    </Alert>
                  )}

                  {/* AVAILABLE DATE */}
                  <TextField
                    fullWidth
                    type="datetime-local"
                    label="Preferred Availability"
                    InputLabelProps={{ shrink: true }}
                    value={formData.available_date}
                    onChange={(e) =>
                      handleChange(
                        "available_date",
                        e.target.value
                      )
                    }
                  />

                  {/* REASON */}
                  <TextField
                    fullWidth
                    select
                    label="Reason for Reschedule"
                    value={formData.reason}
                    onChange={(e) =>
                      handleChange("reason", e.target.value)
                    }
                  >
                    {reasons.map((reason) => (
                      <MenuItem key={reason} value={reason}>
                        {reason}
                      </MenuItem>
                    ))}
                  </TextField>

                  {/* CUSTOM REASON */}
                  {formData.reason === "Other" && (
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Please Specify"
                      value={formData.customReason}
                      onChange={(e) =>
                        handleChange(
                          "customReason",
                          e.target.value
                        )
                      }
                    />
                  )}

                  {/* COMMENTS */}
                  <TextField
                    fullWidth
                    multiline
                    rows={5}
                    label="Additional Comments"
                    placeholder="Add any additional information..."
                    value={formData.comment}
                    onChange={(e) =>
                      handleChange("comment", e.target.value)
                    }
                  />

                  {/* FOOTER */}
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems={{ xs: "stretch", sm: "center" }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      Your request will be reviewed shortly.
                    </Typography>

                    <Button
                      variant="contained"
                      size="large"
                      onClick={handleSubmit}
                      disabled={
                        !formData.reason ||
                        !formData.available_date ||
                        submitting
                      }
                      endIcon={
                        submitting ? (
                          <CircularProgress
                            size={18}
                            sx={{ color: "white" }}
                          />
                        ) : (
                          <SendRoundedIcon />
                        )
                      }
                      sx={{
                        minWidth: 260,
                        height: 52,
                        borderRadius: 3,
                        textTransform: "none",
                        fontWeight: 700,
                        boxShadow: "none",
                      }}
                    >
                      {submitting
                        ? "Submitting..."
                        : "Submit Request"}
                    </Button>
                  </Stack>

                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}